import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class OutletType extends ModelObjectWithDirtyCheck<OutletType> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Type Description
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "OutletType.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class OutletTypeService extends ServiceBase {

  public async getAllOutletTypes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.outletTypes.map((v: any) => new OutletType(v));;
    return this.makeJsonRequest("outletType/v1/getAllOutletTypes", req, false, extractor, opts);
  };

  public async getOutletType(outletTypeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OutletType> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletTypeId:outletTypeId.toString(),
    };
    const extractor = (response:any) => new OutletType(response.outletType);;
    return this.makeJsonRequest("outletType/v1/getOutletType", req, false, extractor, opts);
  };

  public async getOutletTypes(outletTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletTypeIds:outletTypeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.outletTypes.map((v: any) => new OutletType(v));;
    return this.makeJsonRequest("outletType/v1/getOutletTypes", req, false, extractor, opts);
  };

  public async saveOutletTypes(outletTypes: readonly OutletType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletTypes:outletTypes.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("outletType/v1/saveOutletTypes", req, true, extractor, opts);
  };

  public async setOutletTypeIsHidden(outletTypeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletTypeId:outletTypeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("outletType/v1/setOutletTypeIsHidden", req, true, extractor, opts);
  };
}

export const outletTypeService = new OutletTypeService();